import { useEffect, useRef } from "react";

import { basketSyncStep } from "./tpa-balance-refresh.logic";
import type { TpaBalanceBasketSyncOptions } from "./types";

/**
 * Keep the tracker's session usage in step with the cart (COM-674).
 *
 * The balance loader derives usage from the live basket (SFRA parity), but its
 * fetch isn't auto-revalidated after cart mutations — the app re-loads keyed
 * fetchers manually (see useBasketActions). This watches the basket total and
 * calls `reload` when it changes; the rule is `basketSyncStep`. The caller
 * passes the total (domain modules don't read the basket hooks under
 * app/routes/**) — use `useCartSpend().orderCents` (`getOrderTotal`, the same
 * figure the server derives usage from), not raw `orderTotal`: SCAPI omits that
 * until shipping is calculated, so the bar never moved. Not `cents` either: that
 * is the minicart subtotal and misses shipping and reward changes. An empty
 * cart is 0, so the first add to an empty cart still reloads.
 *
 * `enabled` must be true only while a live tracker is on screen.
 */
export function useTpaBalanceBasketSync({
  enabled,
  total,
  reload,
}: TpaBalanceBasketSyncOptions): void {
  const baselineRef = useRef<number | undefined>(undefined);
  // `reload` identity is unstable (not memoized by the balance hook); read the
  // latest through a ref so the effect only re-runs on the total.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    const step = basketSyncStep({
      baseline: baselineRef.current,
      total,
      tracking: enabled,
    });
    baselineRef.current = step.baseline;
    if (step.reload) reloadRef.current();
  }, [total, enabled]);
}
